import { useCallback, useEffect } from 'react';

import { useAui } from '@assistant-ui/react';
import { useLatest } from 'react-use';

import { NEW_CHAT_THREAD_ID } from '@/common/constants';
import { SYNC_EVENTS } from '@/lib/sync/events';
import { useSyncStore } from '@/lib/sync/store';
import { isNewChat } from '@/lib/utils/thread';

import { useCrossTabSync } from './use-cross-tab-sync';
import { useSyncEvent } from './use-sync-event';

/**
 * Normalizes thread IDs for composer text storage.
 *
 * All new threads (before receiving a remote ID) share the same storage key (NEW_CHAT_THREAD_ID).
 * This ensures that:
 * - Users can type in a new thread and immediately switch to another new thread without losing their text
 * - Once a message is sent and the thread gets a remote ID, the composer text is cleared
 * - Each persisted thread maintains its own separate composer state
 */
function getSyncThreadId(threadId: string): string {
  return isNewChat(threadId) ? NEW_CHAT_THREAD_ID : threadId;
}

function updateStoreComposerText(threadId: string, text: string): void {
  if (text) {
    useSyncStore.getState().setComposerText(threadId, text);
  } else {
    useSyncStore.getState().clearComposerText(threadId);
  }
}

export function useSyncedComposer(threadId: string) {
  const api = useAui();
  const syncThreadId = getSyncThreadId(threadId);

  const composerText = useSyncStore(state => state.composerTexts[syncThreadId] ?? '');
  const latestComposerText = useLatest(composerText);
  const { emit } = useCrossTabSync();

  const setComposerTextWithSync = useCallback(
    (value: string | ((prev: string) => string), options: { fromUserInput?: boolean } = {}) => {
      const newValue = typeof value === 'function' ? value(latestComposerText.current) : value;
      updateStoreComposerText(syncThreadId, newValue);

      if (!options.fromUserInput) {
        api.composer.setText(newValue);
      }

      emit(SYNC_EVENTS.COMPOSER_TEXT, { threadId: syncThreadId, text: newValue });
    },
    [syncThreadId, emit, latestComposerText, api],
  );

  useEffect(() => {
    api.composer.setText(composerText);
  }, [syncThreadId, api, composerText]);

  const handleComposerTextSync = useCallback(
    ({ threadId: payloadThreadId, text }: { threadId: string; text: string }) => {
      const normalizedPayloadId = getSyncThreadId(payloadThreadId);
      updateStoreComposerText(normalizedPayloadId, text);

      if (normalizedPayloadId === syncThreadId && text !== latestComposerText.current) {
        api.composer.setText(text);
      }
    },
    [syncThreadId, latestComposerText, api],
  );

  useSyncEvent(SYNC_EVENTS.COMPOSER_TEXT, handleComposerTextSync);

  return {
    composerText,
    setComposerText: setComposerTextWithSync,
  };
}
